import { Component, OnInit, signal, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { loadStripe, Stripe, StripeCardNumberElement, StripeCardExpiryElement, StripeCardCvcElement } from '@stripe/stripe-js';
import { I18NService, OrgPublicInfo, UserService } from '@socrates/socrates-shared';
import { environment } from '../../../environments/environment';

function passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
	const value: string = control.value ?? '';
	const valid = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/.test(value);
	return valid ? null : { passwordStrength: true };
}

function confirmPasswordValidator(group: FormGroup): ValidationErrors | null {
	const password = group.get('password')?.value;
	const confirm  = group.get('confirmPassword')?.value;
	return password === confirm ? null : { passwordMismatch: true };
}

@Component({
	selector: 'app-signup',
	templateUrl: './signup.component.html',
	standalone: false,
})
export class SignupComponent implements OnInit, AfterViewInit {
	readonly submitting        = signal(false);
	readonly orgsLoading       = signal(true);
	readonly submitError       = signal<string | null>(null);
	readonly successMsg        = signal<string | null>(null);
	readonly stripeReady       = signal(false);
	readonly stripeError       = signal<string | null>(null);
	readonly bypassPayment     = environment.bypassPayment ?? false;
	readonly submitAttempted   = signal(false);
	readonly inviteOrgName     = signal<string | null>(null);
	readonly inviteError       = signal<string | null>(null);

	private _inviteToken: string | null = null;

	@ViewChild('cardNumberEl') cardNumberRef!: ElementRef<HTMLDivElement>;
	@ViewChild('cardExpiryEl') cardExpiryRef!: ElementRef<HTMLDivElement>;
	@ViewChild('cardCvcEl')    cardCvcRef!:    ElementRef<HTMLDivElement>;

	orgs: OrgPublicInfo[] = [];
	selectedOrg: OrgPublicInfo | null = null;

	signupForm: FormGroup;

	readonly timezones: string[] = [
		'Pacific/Midway','Pacific/Honolulu','America/Anchorage','America/Los_Angeles',
		'America/Denver','America/Chicago','America/New_York','America/Halifax',
		'America/St_Johns','America/Sao_Paulo','Atlantic/South_Georgia','Atlantic/Azores',
		'UTC','Europe/London','Europe/Paris','Europe/Helsinki','Europe/Moscow',
		'Asia/Dubai','Asia/Karachi','Asia/Dhaka','Asia/Bangkok','Asia/Singapore',
		'Asia/Tokyo','Australia/Sydney','Pacific/Auckland','Pacific/Fiji',
	];

	private stripe: Stripe | null = null;
	private cardNumberElement: StripeCardNumberElement | null = null;
	private cardExpiryElement: StripeCardExpiryElement | null = null;
	private cardCvcElement:    StripeCardCvcElement    | null = null;

	constructor(
		private fb: FormBuilder,
		private userService: UserService,
		private router: Router,
		private route: ActivatedRoute,
	) {}

	async ngOnInit(): Promise<void> {
		this.signupForm = this.fb.group(
			{
				orgId:          ['', Validators.required],
				firstName:      ['', Validators.required],
				lastName:       ['', Validators.required],
				email:          ['', [Validators.required, Validators.email]],
				password:       ['', [Validators.required, passwordStrengthValidator]],
				confirmPassword:['', Validators.required],
				billingName:         [''],
				billingZip:          [''],
				tosAccepted:         [false, Validators.requiredTrue],
				timezone:            [Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''],
				notifyFloorEvents:   [true],
				notifyObjections:    [true],
				notifyDecisions:     [true],
			},
			{ validators: confirmPasswordValidator }
		);

		// When org selection changes, update selectedOrg and mount/unmount card elements.
		this.signupForm.get('orgId').valueChanges.subscribe((id: string) => {
			this.selectedOrg = this.orgs.find((o) => o.id === id) ?? null;
			// Allow one tick for @if(showPaymentField) to render the card containers.
			setTimeout(() => this.mountCardElements(), 0);
		});

		try {
			// Check for invite token first
			const inviteParam = this.route.snapshot.queryParamMap.get('invite');
			if (inviteParam) {
				try {
					const resolved = await this.userService.resolveInvite(inviteParam);
					this._inviteToken = inviteParam;
					this.inviteOrgName.set(resolved.orgName);
					// Immediately set selectedOrg using the resolved org
					this.orgs = await this.userService.listPublicOrgs();
					this.selectedOrg = this.orgs.find((o) => o.id === resolved.orgId) ?? null;
					// Pre-fill org and lock the field
					this.signupForm.get('orgId').setValue(resolved.orgId);
					this.signupForm.get('orgId').disable();
					setTimeout(() => this.mountCardElements(), 0);
				} catch {
					this.inviteError.set(I18NService.getMessage('signup.error.invalidInvite'));
					this.orgs = await this.userService.listPublicOrgs();
				}
			} else {
				this.orgs = await this.userService.listPublicOrgs();

				// Pre-select org from ?orgId= param or if only one exists.
				const paramOrgId = this.route.snapshot.queryParamMap.get('orgId');
				if (paramOrgId && this.orgs.find((o) => o.id === paramOrgId)) {
					this.signupForm.get('orgId').setValue(paramOrgId);
				} else if (this.orgs.length === 1) {
					this.signupForm.get('orgId').setValue(this.orgs[0].id);
				}

				console.log('[signup] orgId after pre-select:', this.signupForm.get('orgId').value, 'orgs:', this.orgs);
			}
		} catch {
			this.submitError.set(I18NService.getMessage('signup.error.loadOrgs'));
		} finally {
			this.orgsLoading.set(false);
		}
	}

	async ngAfterViewInit(): Promise<void> {
		if (environment.stripePublicKey && !environment.stripePublicKey.includes('placeholder')) {
			this.stripe = await loadStripe(environment.stripePublicKey);
		}
	}

	private mountCardElements(): void {
		if (!this.showPaymentField || !this.stripe || this.bypassPayment) return;

		// Destroy existing elements before re-mounting.
		this.destroyCardElements();

		const stripeStyle = {
			base: {
				fontFamily: 'var(--font-family-ui, sans-serif)',
				fontSize: '14px',
				color: '#2d2d2d',
				'::placeholder': { color: '#9ca3af' },
			},
		};

		const elements = this.stripe.elements();

		if (this.cardNumberRef?.nativeElement) {
			this.cardNumberElement = elements.create('cardNumber', { style: stripeStyle, showIcon: true });
			this.cardNumberElement.mount(this.cardNumberRef.nativeElement);
			this.cardNumberElement.on('change', (event) => {
				this.stripeError.set(event.error?.message ?? null);
				this.updateStripeReady();
			});
		}

		if (this.cardExpiryRef?.nativeElement) {
			this.cardExpiryElement = elements.create('cardExpiry', { style: stripeStyle });
			this.cardExpiryElement.mount(this.cardExpiryRef.nativeElement);
			this.cardExpiryElement.on('change', (event) => {
				this.stripeError.set(event.error?.message ?? null);
				this.updateStripeReady();
			});
		}

		if (this.cardCvcRef?.nativeElement) {
			this.cardCvcElement = elements.create('cardCvc', { style: stripeStyle });
			this.cardCvcElement.mount(this.cardCvcRef.nativeElement);
			this.cardCvcElement.on('change', (event) => {
				this.stripeError.set(event.error?.message ?? null);
				this.updateStripeReady();
			});
		}
	}

	private destroyCardElements(): void {
		if (this.cardNumberElement) { this.cardNumberElement.unmount(); this.cardNumberElement.destroy(); this.cardNumberElement = null; }
		if (this.cardExpiryElement) { this.cardExpiryElement.unmount(); this.cardExpiryElement.destroy(); this.cardExpiryElement = null; }
		if (this.cardCvcElement)    { this.cardCvcElement.unmount();    this.cardCvcElement.destroy();    this.cardCvcElement    = null; }
		this.stripeReady.set(false);
	}

	private updateStripeReady(): void {
		// Considered ready when all three elements are complete (no errors)
		// Stripe doesn't expose a .complete per-element in real-time easily,
		// so we rely on absence of errors and let the createPaymentMethod call validate.
		this.stripeReady.set(!!this.cardNumberElement && !!this.cardExpiryElement && !!this.cardCvcElement);
	}

	get showOrgSelector(): boolean {
		return this.orgs.length > 1;
	}

	get tosVersion(): string {
		return new Date().toISOString().slice(0, 10);
	}

	get showPaymentField(): boolean {
		return !!this.selectedOrg?.isPaymentRequired;
	}

	get chargeLabel(): string {
		if (!this.selectedOrg?.isPaymentRequired) return '';
		const amount = parseFloat(this.selectedOrg.chargeAmount as any) || 0;
		const freq   = this.selectedOrg.isMonthlyCharge ? I18NService.getMessage('signup.billing.monthly') : I18NService.getMessage('signup.billing.oneTime');
		return ` $${amount.toFixed(2)} ${freq}`;
	}

	async onSubmit(): Promise<void> {
		this.submitError.set(null);
		this.submitAttempted.set(true);
		if (this.signupForm.invalid) {
			this.signupForm.markAllAsTouched();
			const invalid = Object.entries(this.signupForm.controls)
				.filter(([, c]) => c.invalid)
				.map(([k, c]) => ({ key: k, errors: c.errors }));
			console.warn('Signup form invalid controls:', invalid, 'Form errors:', this.signupForm.errors);
			return;
		}
		this.submitting.set(true);
		try {
			const { orgId: orgIdValue, email, password, firstName, lastName, billingName, billingZip,
			timezone, notifyFloorEvents, notifyObjections, notifyDecisions } = this.signupForm.getRawValue();
			const orgId = orgIdValue;

			let stripePaymentMethodId: string | undefined;
			if (this.showPaymentField && !this.bypassPayment) {
				if (!this.stripe || !this.cardNumberElement) {
					this.submitError.set(I18NService.getMessage('signup.error.paymentNotReady'));
					return;
				}
				const cardholderName = billingName || `${firstName} ${lastName}`;
				const { paymentMethod, error } = await this.stripe.createPaymentMethod({
					type: 'card',
					card: this.cardNumberElement,
					billing_details: {
						name: cardholderName,
						email,
						address: billingZip ? { postal_code: billingZip } : undefined,
					},
				});
				if (error) {
					this.submitError.set(error.message ?? I18NService.getMessage('signup.error.paymentFailed'));
					return;
				}
				stripePaymentMethodId = paymentMethod!.id;
			}

			const tosVersion = new Date().toISOString().slice(0, 10);
			const result = await this.userService.signup({
				orgId, email, password, firstName, lastName,
				stripePaymentMethodId,
				billingName: billingName || undefined,
				billingZip: billingZip || undefined,
				tosVersion,
				timezone: timezone || undefined,
				notifyFloorEvents,
				notifyObjections,
				notifyDecisions,
				inviteToken: this._inviteToken ?? undefined,
			});
			this.successMsg.set(result.message);
			if (result.joinMode === 'OPEN') {
				setTimeout(() => this.router.navigate(['/login']), 3000);
			}
		} catch (err: any) {
			this.submitError.set(err?.error?.error ?? I18NService.getMessage('signup.error.generic'));
		} finally {
			this.submitting.set(false);
		}
	}

	getMessage(key: string): string {
		return I18NService.getMessage(key);
	}

	onCancel(): void {
		this.router.navigate(['/login']);
	}
}
